/**
 * Where do two links cross, and which one should say so.
 *
 * On a plant diagram a bare crossing is a genuine misread: two streams that never touch look
 * connected.  Indicating one of them -- interrupting the link underneath, or bridging over it --
 * is what makes the diagram legible without zooming.
 *
 * Routes are orthogonal, so a crossing reduces to a horizontal segment meeting a vertical one.
 * Pure geometry over plain data: no React, no DOM, no layout engine.
 */
import type { Point } from "./layout";

export type Axis = "h" | "v";

export type Segment = {
    /** Index of the owning route in the input array. Doubles as paint order. */
    route: number;
    /** Index of this segment within its route, i.e. it runs points[index] -> points[index + 1]. */
    index: number;
    axis: Axis;
    /** Ordered along the axis of travel, so from/to preserve direction. */
    from: Point;
    to: Point;
};

export type Hop = {
    /** Which segment of the owning route the hop sits on. */
    segment: number;
    x: number;
    y: number;
    /**
     * Stroke width the break has to clear: the wider of the two links at this crossing, so one
     * radius works whichever of them is underneath.  Grows when two crossings merge.
     */
    clearance: number;
};

export type RouteInput = {
    id: string;
    points: Point[];
    /** Stroke width, used to size the break. */
    width: number;
    /** Contributes segments others can cross. A link with no geometry cannot. */
    eligible: boolean;
    /** Wants an indicator of its own. A link can be an obstacle without drawing anything. */
    wantsHops: boolean;
    source: string;
    target: string;
};

/** Below this, two coordinates count as equal. ELK emits exact values; synthesised routes round. */
const TOLERANCE = 0.5;

/** Extra clearance either side of a break, on top of half the crossed stroke. */
export const MARGIN = 4;

/** Below this a bridge is too small to read, so it is drawn at this radius instead. */
export const HOP_MIN_RADIUS = 5;

/** Half-width the geometry asks for. What is DRAWN is `drawnRadius`, which floors this. */
export const clearanceRadius = (clearance: number) => clearance / 2 + MARGIN;

/**
 * Half-width of the break as `edgeTypes/path.ts` actually draws it.
 *
 * Merging has to use the number the renderer will use, not the one the geometry asked for: with
 * sub-2px strokes on both links the raw radius is 4.5 while the drawn one is floored to 5, so two
 * crossings 9-10px apart on one segment were left unmerged and the drawn path doubled back over
 * itself by up to a pixel.
 */
export const drawnRadius = (clearance: number) => Math.max(HOP_MIN_RADIUS, clearanceRadius(clearance));

/** The clearance a break of this radius implies -- the inverse of `clearanceRadius`. */
const radiusClearance = (radius: number) => 2 * (radius - MARGIN);

/** Tolerance for treating a crossing as sitting on a node's border rather than clear of it. */
const NODE_EDGE_TOLERANCE = 2;

/**
 * Split a polyline into axis-aligned segments.
 *
 * Anything neither horizontal nor vertical is dropped rather than guessed at: orthogonal routing
 * should never produce one, and a diagonal would silently break the intersection maths.
 */
const toSegments = (points: Point[], route = 0): Segment[] => {
    const segments: Segment[] = [];

    for (let i = 0; i < points.length - 1; i++) {
        const from = points[i];
        const to = points[i + 1];
        const horizontal = Math.abs(from.y - to.y) <= TOLERANCE;
        const vertical = Math.abs(from.x - to.x) <= TOLERANCE;

        // A zero-length segment is both; it can be neither crossed nor cross anything.
        if (horizontal && vertical) continue;

        if (horizontal) {
            // Snap the constant coordinate so the intersection test is exact.
            const y = (from.y + to.y) / 2;
            segments.push({ route, index: i, axis: "h", from: { x: from.x, y }, to: { x: to.x, y } });
        } else if (vertical) {
            const x = (from.x + to.x) / 2;
            segments.push({ route, index: i, axis: "v", from: { x, y: from.y }, to: { x, y: to.y } });
        }
    }

    return segments;
};

const spans = (a: number, b: number) => (a < b ? { min: a, max: b } : { min: b, max: a });

/** Whether a point lies in the box spanned by two others, grown by `margin` on every side. */
const between = (point: Point, one: Point, two: Point, margin: number) =>
    point.x >= Math.min(one.x, two.x) - margin && point.x <= Math.max(one.x, two.x) + margin &&
    point.y >= Math.min(one.y, two.y) - margin && point.y <= Math.max(one.y, two.y) + margin;

/**
 * Where a horizontal segment crosses a vertical one, if it does.
 *
 * Strictly inside both, by `margin`.  That single condition does three jobs: it excludes
 * T-junctions where segments merely touch, it keeps a break away from a corner where it would
 * deform the turn, and it keeps one out from under an arrowhead.
 */
const intersect = (horizontal: Segment, vertical: Segment, margin: number): Point | undefined => {
    const x = vertical.from.x;
    const y = horizontal.from.y;
    const across = spans(horizontal.from.x, horizontal.to.x);
    const along = spans(vertical.from.y, vertical.to.y);

    const inside = x > across.min + margin && x < across.max - margin
        && y > along.min + margin && y < along.max - margin;

    return inside ? { x, y } : undefined;
};

/**
 * Which of two routes indicates the crossing.
 *
 * The later one in the array: a break drawn by edge N lands on top of edge N-1's stroke and
 * underneath N's own.  That is the whole mechanism, and it holds only while every edge paints at
 * the same depth -- so the component renders react-flow in `zIndexMode="manual"` and nothing here
 * or in `edgeTypes` ever sets an edge's `zIndex`.  Left to itself v12 gives an edge a z-index that
 * includes the nesting depth of its endpoints, which would let a link inside a band paint over one
 * outside it whatever the array says, and a crossing would then be indicated by the link
 * underneath -- silently, since the break is still drawn, just covered.
 *
 * Array order comes from the depth-first walk over the authored input, so it is a pure function
 * of the JSON and stable across relayouts.  Two segments of the SAME route never reach here, so
 * the orders always differ.
 */
const owner = (a: RouteInput & { order: number }, b: RouteInput & { order: number }) =>
    a.order > b.order ? a : b;

/**
 * Find every crossing and assign it to the route that should indicate it.
 *
 * Deliberately a double loop rather than a sweep line: the realistic worst case here is a couple
 * of hundred links of a handful of segments each, so a few hundred thousand comparisons, once per
 * layout, in well under a millisecond.  Please leave it alone unless a profile says otherwise.
 */
export const computeHops = (
    routes: RouteInput[],
    nodeRects: { x: number; y: number; width: number; height: number }[] = []
): Map<string, Hop[]> => {
    const indexed = routes.map((route, order) => ({ ...route, order }));
    const segments = indexed.flatMap((route, order) => (route.eligible ? toSegments(route.points, order) : []));
    const horizontals = segments.filter(s => s.axis === "h");
    const verticals = segments.filter(s => s.axis === "v");

    const hops = new Map<string, Hop[]>();

    for (const horizontal of horizontals) {
        for (const vertical of verticals) {
            const a = indexed[horizontal.route];
            const b = indexed[vertical.route];

            if (a.order === b.order) continue;

            const clearance = Math.max(a.width, b.width);
            // The raw radius, not `drawnRadius`.  `intersect`'s margin is partly about the size
            // the break is DRAWN at, so in principle this should use the same floor -- but the
            // two differ by at most 0.5px, and only for sub-2px strokes, whereas raising the
            // margin here would reject genuine crossings outright.  A sub-pixel overhang at a
            // segment end is the cheaper of the two errors.
            const margin = clearanceRadius(clearance);
            const point = intersect(horizontal, vertical, margin);
            if (!point) continue;

            // Two links leaving the same node, or arriving at the same one, are one signal
            // branching -- a bridge where they part company says they cross when they do not,
            // and without this every fan-out sprouts one.
            //
            // But only where they PART.  This used to suppress the pair outright, anywhere along
            // either path, and two links that leave the same node, diverge across the chart and
            // meet again hundreds of pixels later are crossing in every sense a reader cares
            // about.  Those are the most visible crossings on a fanned-out chart, and they were
            // the ones going unmarked -- worst of all where the fan is colour-coded, since then
            // the two lines are identical and nothing whatsoever separates them.
            //
            // The branch is the box between the two links' own ends at the node they share:
            // geometry already to hand, and no guess at how far a fan spreads.
            const sharedSource = a.source === b.source;
            if (sharedSource || a.target === b.target) {
                const end = (route: typeof a) =>
                    (sharedSource ? route.points[0] : route.points[route.points.length - 1]);

                if (between(point, end(a), end(b), margin)) continue;
            }

            // Nodes paint above edges, so a bridge under one would only peek out at its border.
            if (nodeRects.some(r =>
                point.x > r.x - NODE_EDGE_TOLERANCE && point.x < r.x + r.width + NODE_EDGE_TOLERANCE &&
                point.y > r.y - NODE_EDGE_TOLERANCE && point.y < r.y + r.height + NODE_EDGE_TOLERANCE)) {
                continue;
            }

            const winner = owner(a, b);
            if (!winner.wantsHops) continue;

            const segment = winner.order === horizontal.route ? horizontal : vertical;
            const existing = hops.get(winner.id) ?? [];
            existing.push({ segment: segment.index, x: point.x, y: point.y, clearance });
            hops.set(winner.id, existing);
        }
    }

    // Order along each segment and merge any that would overlap: one wider break reads better
    // than two touching ones, and overlapping arcs produce a self-intersecting path.
    for (const [id, list] of hops) {
        const bySegment = new Map<number, Hop[]>();
        for (const hop of list) {
            bySegment.set(hop.segment, [...(bySegment.get(hop.segment) ?? []), hop]);
        }

        const merged: Hop[] = [];
        for (const [, group] of bySegment) {
            group.sort((p, q) => (p.x - q.x) || (p.y - q.y));

            for (const hop of group) {
                const last = merged[merged.length - 1];
                const gap = last ? Math.hypot(last.x - hop.x, last.y - hop.y) : Infinity;
                const lastRadius = last ? drawnRadius(last.clearance) : 0;
                const radius = drawnRadius(hop.clearance);

                if (last && last.segment === hop.segment && gap < lastRadius + radius) {
                    // One break replacing two has to SPAN them, not sit between them at the
                    // radius of either: the union runs from `last - lastRadius` to
                    // `hop + radius`, so the merged break is that wide and centred on it.
                    // Averaging the two centres and keeping one radius covered neither.
                    const span = gap + lastRadius + radius;
                    const centre = (span / 2) - lastRadius;
                    const t = gap === 0 ? 0 : centre / gap;

                    last.x += (hop.x - last.x) * t;
                    last.y += (hop.y - last.y) * t;
                    last.clearance = radiusClearance(span / 2);
                    continue;
                }
                merged.push({ ...hop });
            }
        }

        hops.set(id, merged);
    }

    return hops;
};

/**
 * Exported for `crossings.test.ts` only.  Not published: `library_build` sets `stripInternal`, so
 * this statement is dropped from the emitted `.d.ts` -- see the note at the foot of `layout.ts`.
 *
 * @internal
 */
export { toSegments, intersect };
